import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RecordPanel } from './RecordPanel';
import { formatRand } from '@/lib/contact';
import type { RecordsApi, RecService } from '@/lib/records';

/**
 * Past services (Gary, 18 Oct): newest first, each with what was done, the amount, the operator and the invoice,
 * paid or unpaid. An unpaid one copies its payment link to paste in the chat. All services, the old systems' included.
 */
export function HistoryPanel({ api, name, onClose, onCopied }: {
  api: RecordsApi; name: string; onClose: () => void; onCopied: () => void;
}) {
  const [rows, setRows] = useState<RecService[] | null>(null);
  const [open, setOpen] = useState<RecService | null>(null);
  // Copying blocked (some iPhone browsers): the message shows to select by hand.
  const [blocked, setBlocked] = useState<string | null>(null);
  // Back from an invoice returns to the same place in a long list (mobile review, 19 Oct: it jumped to the top).
  const body = useRef<HTMLDivElement>(null);
  const listTop = useRef(0);
  useLayoutEffect(() => {
    if (body.current) body.current.scrollTop = open ? 0 : listTop.current;
  }, [open]);
  useEffect(() => { void api.history().then(setRows).catch(() => setRows([])); }, [api]);
  const owed = (rows ?? []).filter((s) => !s.paid && s.invoice).reduce((t, s) => t + (s.amount ?? 0), 0);

  if (open) {
    const message = `Here is the link to pay invoice ${open.invoice}${open.amount !== null ? ` (${formatRand(open.amount)})` : ''}: ${open.payUrl}`;
    // Says what it does (mobile review, 19 Oct: "Put ... in the chat" only copied), and only says copied once it is.
    const copy = () => {
      if (!navigator.clipboard) { setBlocked(message); return; }
      navigator.clipboard.writeText(message).then(onCopied, () => setBlocked(message));
    };
    return (
      <RecordPanel title={month(open.date)} sub={[open.invoice, open.invoice ? (open.paid ? 'Paid' : 'Unpaid') : null].filter(Boolean).join(' · ')} onClose={onClose}
        onBack={() => { setBlocked(null); setOpen(null); }} bodyRef={body}
        footer={!open.paid && open.payUrl ? <button type="button" className="chats-btn chats-rec-wide" onClick={copy}>Copy payment link</button> : undefined}>
        {blocked && (
          <>
            <p className="chats-rec-error" role="alert">Copying is blocked here. Press and hold the message below to copy it.</p>
            <p className="chats-rec-notes">{blocked}</p>
          </>
        )}
        <dl className="chats-rec-list">
          <div><dt>Done</dt><dd>{month(open.date)}{open.operator ? ` by ${open.operator}` : ''}</dd></div>
          <div><dt>What</dt><dd>{open.items}</dd></div>
          {open.amount !== null && <div><dt>Amount</dt><dd>{formatRand(open.amount)}</dd></div>}
          <div><dt>Invoice</dt><dd>{open.invoice ?? 'None'}</dd></div>
          {open.invoice && <div><dt>Status</dt><dd><span className={`chats-rec-status chats-rec-status--${open.paid ? 'completed' : 'unpaid'}`}>{open.paid ? 'Paid' : 'Unpaid'}</span></dd></div>}
        </dl>
      </RecordPanel>
    );
  }

  return (
    <RecordPanel title="Service history" sub={`${name}${rows ? ` · ${rows.length} service${rows.length === 1 ? '' : 's'}` : ''}`} onClose={onClose} bodyRef={body}>
      {owed > 0 && <p className="chats-rec-owed">Owes {formatRand(owed)}</p>}
      {rows === null && <p className="chats-rec-hint">Loading…</p>}
      {rows?.length === 0 && <p className="chats-rec-hint">No services yet.</p>}
      {!!rows?.length && (
        <div className="chats-rec-card chats-rec-card--rows">
          {rows.map((s) => (
            <button key={s.key} type="button" className="chats-rec-row" onClick={() => { listTop.current = body.current?.scrollTop ?? 0; setOpen(s); }}>
              <span className="chats-rec-row-main">
                <b>{month(s.date)}{s.amount !== null ? ` · ${formatRand(s.amount)}` : ''}</b>
                <small>{s.items}{s.operator ? ` · ${s.operator.split(' ')[0]}` : ''}</small>
              </span>
              {s.invoice && <span className={`chats-rec-status chats-rec-status--${s.paid ? 'completed' : 'unpaid'}`}>{s.paid ? 'Paid' : 'Unpaid'}</span>}
            </button>
          ))}
        </div>
      )}
    </RecordPanel>
  );
}

const month = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
